import React, { useState, useEffect, useMemo } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowId, GridRowParams, deDE } from "@mui/x-data-grid";
import { IProject } from "../../../interfaces/IProject";
import { uploadFetch } from "../../../hooks/useFetch";
import { IFeedback } from "../../../interfaces/IFeedback";
import { CustomSnackbar } from "../../core/Snackbar";
import { IProfile, IUser } from "../../../interfaces/IUser";
import { IWorker } from "../../../interfaces/IWorker";
import { WorkerTableRow } from "./WorkerTableRow";
import { getEmptyProfile } from "../../../sites/RoleManagement/RoleManagementOverview";

const mapRoleToProjectRole = (roleId: number | undefined): number => {
    if ([4, 6, 7].includes(roleId ?? 0)) return roleId!;

    switch (roleId) {
        case 1:
            return 6; // Geschäftsführer → Außendienst
        case 2:
            return 4; // GF Assistenz → Innendienst
        case 3:
            return 4; // Teamleiter Innendienst → Innendienst
        case 5:
            return 6; // Teamleiter Außendienst → Außendienst
        default:
            return 4; // Fallback
    }
};

interface IProps {
    projectObject: IProject;
    setProjectObject: Function;
    userArray: IProfile[];
    workerArray: IWorker[];
}

export const WorkerTable: React.FC<IProps> = ({ projectObject, setProjectObject, workerArray, userArray }) => {
    const [isOpenAdd, setIsOpenAdd] = useState(false);
    const [addNewWorker, setAddNewWorker] = useState<number[]>([]);
    const [selectedWorkers, setSelectedWorkers] = useState<{ Profile: IProfile; ProjectRole: number }[]>([]);
    const [feedback, setFeedback] = useState<IFeedback>({ open: false, message: "", severity: "info" });

    const columns: GridColDef[] = useMemo(
        () => [
            { field: "number", headerName: "Nr.", valueGetter: (params) => params.row.id, width: 70 },
            {
                field: "username",
                headerName: "Benutzername",
                valueGetter: (params) => params.row.User.username,
                width: 200,
            },
            {
                field: "first_name",
                headerName: "Vorname",
                valueGetter: (params) => params.row.User.first_name,
                width: 200,
            },
            { field: "last_name", headerName: "Name", valueGetter: (params) => params.row.User.last_name, width: 200 },
            {
                field: "projectRole",
                headerName: "Projektrolle",
                width: 180,
                renderCell: (params) => {
                    const profileId = params.row.id;
                    const selected = selectedWorkers.find((sw) => sw.Profile.id === profileId);
                    if (!selected) return null;

                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const updated = selectedWorkers.map((sw) =>
                            sw.Profile.id === profileId ? { ...sw, ProjectRole: Number(e.target.value) } : sw,
                        );
                        setSelectedWorkers(updated);
                    };

                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <TextField
                                size="small"
                                value={selected.ProjectRole}
                                onChange={handleChange}
                                variant="outlined"
                                select
                                fullWidth
                            >
                                {[4, 6, 7].map((id) => (
                                    <MenuItem key={id} value={id}>
                                        {id === 4 ? "Innendienst" : id === 6 ? "Außendienst" : "Bauleiter"}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                    );
                },
            },
        ],
        [selectedWorkers, setSelectedWorkers],
    );

    const handleAddNew = () => {
        setAddNewWorker([]);
        setSelectedWorkers([]);
        setIsOpenAdd(true);
    };

    const handleRowSelectionChange = (newRowSelectionModel: GridRowId[]) => {
        const selectedProfiles = newRowSelectionModel.map((id) => {
            const profile = userArray.find((p) => p.id === Number(id))!;
            const existing = selectedWorkers.find((sw) => sw.Profile.id === profile.id);
            const mappedRole = mapRoleToProjectRole(profile.Role);

            return {
                Profile: profile,
                ProjectRole: existing?.ProjectRole ?? mappedRole,
            };
        });

        setAddNewWorker(newRowSelectionModel.map(Number));
        setSelectedWorkers(selectedProfiles);
    };

    const handleAddBtn = () => {
        const newWorkers: IWorker[] = selectedWorkers.map((entry) => {
            const roleId = entry.ProjectRole;
            const roleName = roleId === 4 ? "Innendienst" : roleId === 6 ? "Außendienst" : "Bauleiter";
            return {
                Profile: entry.Profile,
                ProjectRole: { id: roleId, role: roleName },
            };
        });

        setWorkerArray(newWorkers, "add");
        setIsOpenAdd(false);
    };

    const setWorkerArray = (newWorkers: IWorker | IWorker[], action: "add" | "remove" | "update") => {
        if (action === "add") {
            const toAdd = Array.isArray(newWorkers) ? newWorkers : [newWorkers];
            setProjectObject({
                ...projectObject,
                Worker: [
                    ...projectObject.Worker,
                    ...toAdd.filter((nw) => !projectObject.Worker.some((w) => w.Profile.id === nw.Profile.id)),
                ],
            });
        } else if (action === "remove") {
            const removeId = Array.isArray(newWorkers) ? null : newWorkers.Profile.id;
            if (!removeId) return;
            const filtered = projectObject.Worker.filter((w) => w.Profile.id !== removeId);
            setProjectObject({
                ...projectObject,
                Worker: filtered,
            });
        } else if (action === "update" && !Array.isArray(newWorkers)) {
            const updated = projectObject.Worker.map((w) => (w.Profile.id === newWorkers.Profile.id ? newWorkers : w));
            setProjectObject({
                ...projectObject,
                Worker: updated,
            });
        } else {
            console.error("Invalid action or format for setWorkerArray");
        }
    };

    return (
        <>
            <CustomSnackbar feedback={feedback} setFeedback={setFeedback} />
            <Dialog open={isOpenAdd} onClose={() => setIsOpenAdd(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Mitarbeiter hinzufügen</DialogTitle>
                <DialogContent>
                    <DataGrid
                        sx={{ mt: 2 }}
                        autoHeight
                        checkboxSelection
                        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                        rows={userArray}
                        onRowSelectionModelChange={handleRowSelectionChange}
                        rowSelectionModel={addNewWorker}
                        columns={columns}
                        isRowSelectable={(params: GridRowParams) =>
                            !projectObject.Worker.some((w) => w.Profile.id === params.id)
                        }
                        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setIsOpenAdd(false)}>
                        Abbruch
                    </Button>
                    <Button variant="contained" onClick={handleAddBtn}>
                        Hinzufügen
                    </Button>
                </DialogActions>
            </Dialog>

            <Typography variant="h6" sx={{ mt: 3 }}>
                Zugeordnete Mitarbeiter
                <Button variant="outlined" sx={{ float: "right" }} onClick={handleAddNew}>
                    Mitarbeiter zuordnen
                </Button>
            </Typography>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Nr.</TableCell>
                        <TableCell>Mitarbeiter</TableCell>
                        <TableCell>Rolle</TableCell>
                        <TableCell>Mobil</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {projectObject.Worker.map((worker) => (
                        <WorkerTableRow
                            key={`worker-${worker.Profile.id}`}
                            worker={worker}
                            setWorkerArray={setWorkerArray}
                        />
                    ))}
                </TableBody>
            </Table>
        </>
    );
};
